import {
  addToast,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
} from '@heroui/react'
import { Clock, Send, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

function RetryMessageModal({ isOpen, onClose, message, onRetry }) {
  const [editedMessage, setEditedMessage] = useState('')
  const [isRetrying, setIsRetrying] = useState(false)
  const [localIsOpen, setLocalIsOpen] = useState(false)
  const modalStateRef = useRef(false)

  // Sync the external and local open states
  useEffect(() => {
    if (isOpen && !modalStateRef.current) {
      setLocalIsOpen(true)
      modalStateRef.current = true
    }
  }, [isOpen])

  // Update the message when the message prop changes
  useEffect(() => {
    if (message) {
      setEditedMessage(message.message || message.body || '')
    }
  }, [message])

  const handleClose = () => {
    setLocalIsOpen(false)
    modalStateRef.current = false
    // Simple close without delay for better responsiveness
    onClose()
  }

  const handleRetrySend = async () => {
    if (!message)
      return

    setIsRetrying(true)

    try {
      await onRetry({
        agent_number: message.from || message.sender_number,
        user_number: message.to || message.recipient_number,
        agent_message: editedMessage,
      })

      // Show success toast
      addToast({
        title: 'Message sent successfully',
        color: 'success',
      })

      // Close the modal immediately on success
      handleClose()
    }
    catch (error) {
      console.error('Error resending message:', error)

      // Show error toast
      addToast({
        title: 'Failed to send message',
        description: error.message || 'An unknown error occurred',
        color: 'danger',
      })

      setIsRetrying(false)
    }
  }

  return (
    <Modal
      isOpen={localIsOpen}
      onOpenChange={(open) => {
        if (!open)
          handleClose()
      }}
      radius="none"
      placement="center"
      isDismissable={false}
      hideCloseButton
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex items-center justify-between">
              <span>Retry Message</span>
              <Button
                isIconOnly
                variant="light"
                onPress={handleClose}
                className={`
                  text-gray-500
                  hover:text-gray-700
                `}
                disabled={isRetrying}
              >
                <X size={20} />
              </Button>
            </ModalHeader>
            <ModalBody>
              <div className="mb-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Edit your message (optional)
                </label>
                <Textarea
                  value={editedMessage}
                  onChange={e => setEditedMessage(e.target.value)}
                  placeholder="Type your message here..."
                  rows={4}
                  className="w-full"
                  disabled={isRetrying}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="light"
                onPress={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700"
                disabled={isRetrying}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                radius="none"
                onPress={handleRetrySend}
                isDisabled={isRetrying || !editedMessage.trim()}
                className="px-4 py-2 text-sm font-medium"
                startContent={isRetrying
                  ? (
                      <Clock size={16} className="animate-spin" />
                    )
                  : (
                      <Send size={16} />
                    )}
              >
                {isRetrying ? 'Sending...' : 'Send'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}

export default RetryMessageModal
